import { MailHeader } from 'components/mail/mail-header'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Navigate, useSearchParams } from 'react-router-dom'
import { AppShell } from 'components/dashboard/app-shell'
import { useUser } from 'contexts/UserContext'
import { Button } from 'components/ui/button'
import { Input } from 'components/ui/input'
import { Label } from 'components/ui/label'
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card'
import { Alert, AlertDescription } from 'components/ui/alert'
import { MailApiError, Mailbox, MailboxInput, MailRun, createMailbox, listMailRuns, listMailboxes, patchMailbox, startMailOAuth, syncMailbox } from 'services/mail'
import { useMailboxIndexing } from 'hooks/useMailboxIndexing'
import { MailIndexProgress } from 'components/mail/MailIndexProgress'

const defaults: MailboxInput = {
  name: '', address: '', visibility: 'personal', provider: 'generic', auth_kind: 'password', username: '',
  imap_host: '', imap_port: 993, imap_security: 'tls', smtp_host: '', smtp_port: 587, smtp_security: 'starttls',
  folders: [], initial_since: null, max_messages_per_run: 100, max_message_bytes: 10485760,
  ai_indexing_enabled: false,
  max_attachment_bytes: 5242880, sync_interval_seconds: 300, timeout_seconds: 30, sent_folder: '',
}

function formFrom(mailbox: Mailbox): MailboxInput {
  const fields = Object.keys(defaults) as (keyof MailboxInput)[]
  const result = { ...defaults }
  for (const field of fields) (result as any)[field] = (mailbox as any)[field]
  return result
}

const numeric = new Set(['imap_port', 'smtp_port', 'max_messages_per_run', 'max_message_bytes', 'max_attachment_bytes', 'sync_interval_seconds', 'timeout_seconds'])
const terminal = new Set(['succeeded', 'partial', 'failed', 'interrupted'])
const indexingLabel = (box: Mailbox) => !box.ai_indexing_enabled ? 'AI disattivata'
  : box.ai_indexing_effective ? 'AI attiva' : 'AI abilitata, bloccata dalla configurazione generale'

export default function MailAccountsPage() {
  const { user, loading: userLoading } = useUser()
  const [params, setParams] = useSearchParams()
  const requestedId = useRef(params.get('mailbox')); requestedId.current = params.get('mailbox')
  const saveLock = useRef(false)
  const [oauthIncomplete, setOauthIncomplete] = useState(false), [creationAmbiguous, setCreationAmbiguous] = useState(false)
  const [step, setStep] = useState(0), [advancedOpen, setAdvancedOpen] = useState(false)
  const [mailboxes, setMailboxes] = useState<Mailbox[]>([])
  const [selected, setSelected] = useState<Mailbox | null>(null)
  const [form, setForm] = useState<MailboxInput>({ ...defaults })
  const [folderText, setFolderText] = useState('')
  const [runs, setRuns] = useState<MailRun[]>([])
  const [pollCycle, setPollCycle] = useState(0)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [conflict, setConflict] = useState(false)
  const requestKey = useRef(0)
  const indexing = useMailboxIndexing(selected, requestKey.current)
  const indexingStatus = indexing.status
  const selectedId = selected?.id

  const report = (err: unknown) => {
    const apiError = err as MailApiError
    setFieldErrors(apiError.fields || {})
    setConflict(apiError.status === 409 && apiError.code === 'conflict')
    setError(apiError.status === 403 || apiError.status === 404 ? 'Accesso alla casella non consentito o revocato.' : apiError.message || 'Operazione non riuscita.')
  }

  const reload = useCallback(async () => {
    const key = ++requestKey.current
    setLoading(true)
    setError('')
    try {
      const page = await listMailboxes()
      if (key !== requestKey.current) return
      setMailboxes(page.results)
      setSelected(current => {
        const fresh = page.results.find(box => box.id === (current?.id || requestedId.current)) || null
        if (fresh) { setForm(formFrom(fresh)); setFolderText(fresh.folders.join('\n')) }
        return fresh
      })
    } catch (err) { if (key === requestKey.current) report(err) }
    finally { if (key === requestKey.current) setLoading(false) }
  }, [])

  useEffect(() => { if (!userLoading && user?.enabled_modules?.emails) reload(); return () => { requestKey.current++ } }, [userLoading, user?.enabled_modules?.emails, reload])

  const choose = (box: Mailbox | null) => {
    requestKey.current++
    setStep(0); setAdvancedOpen(false); setOauthIncomplete(false); setCreationAmbiguous(false)
    setSelected(box)
    setForm(box ? formFrom(box) : { ...defaults })
    setFolderText(box?.folders.join('\n') || '')
    setRuns([]); setError(''); setMessage(''); setConflict(false); setFieldErrors({})
  }

  useEffect(() => {
    if (!selectedId || !selected?.permissions['mail.read']) return
    let cancelled = false
    const id = selectedId
    let timer: number | undefined
    let remaining = 18
    const fetchRuns = async () => {
      try {
        const page = await listMailRuns(id)
        if (cancelled) return
        setRuns(page.results); setError('')
        if (page.results.some(run => !terminal.has(run.status)) && remaining-- > 0) {
          timer = window.setTimeout(fetchRuns, 10000)
        }
      } catch (err) {
        if (!cancelled) { setRuns([]); report(err) }
      }
    }
    fetchRuns()
    return () => { cancelled = true; window.clearTimeout(timer) }
  }, [selectedId, selected?.permissions, pollCycle])

  useEffect(() => {
    const key = Object.keys(fieldErrors)[0]
    if (!key) return
    const identity = ['name', 'address', 'visibility'].includes(key)
    const provider = key === 'provider'
    const desired = provider ? 0 : identity ? 1 : 2
    if (!selected && step !== desired) { setStep(desired); return }
    if (['max_messages_per_run', 'max_message_bytes', 'max_attachment_bytes', 'sync_interval_seconds', 'timeout_seconds'].includes(key) && !advancedOpen) { setAdvancedOpen(true); return }
    document.getElementById(`mail-${key}`)?.focus()
  }, [fieldErrors, step, advancedOpen, selected?.id])

  if (userLoading) return <p role="status">Caricamento accesso…</p>
  if (!user) return <Navigate to="/login" replace />
  if (!user.enabled_modules?.emails) return <Navigate to="/dashboard-new" replace />

  const mayManage = !selected || (selected.permissions['mail.manage'] && (selected.owner === user.id || selected.visibility === 'shared'))
  const canSync = !!selected?.permissions['mail.sync']
  const update = (key: keyof MailboxInput, value: unknown) => setForm(current => ({ ...current, [key]: value, ...(key === 'provider' ? { auth_kind: value === 'gmail' ? 'oauth2' : 'password', ...(value === 'gmail' ? { password: '' } : {}) } : {}) }))

  const openOAuth = async (id: string) => {
    const key = requestKey.current
    const result = await startMailOAuth(id)
    if (key !== requestKey.current) return
    const destination = new URL(result.authorization_url)
    if (destination.protocol !== 'https:' || destination.hostname !== 'accounts.google.com') throw new Error('Destinazione OAuth non valida.')
    window.location.assign(destination.toString())
  }
  const save = async (event: React.FormEvent) => {
    event.preventDefault()
    if (busy || saveLock.current || creationAmbiguous || !mayManage) return
    saveLock.current = true
    let key = requestKey.current
    setBusy(true); setError(''); setMessage(''); setFieldErrors({}); setConflict(false)
    const sentFolder = form.sent_folder.trim()
    const payload = { ...form, sent_folder: sentFolder, folders: Array.from(new Set([...folderText.split(/\r?\n/).map(value => value.trim()).filter(Boolean), ...(sentFolder ? [sentFolder] : [])])) }
    if (!payload.password) delete payload.password
    const creating = !selected
    let createdId: string | null = null
    try {
      const fresh = selected ? await patchMailbox(selected.id, { ...payload, config_version: selected.config_version }) : await createMailbox(payload)
      if (key !== requestKey.current) return
      createdId = creating ? fresh.id : null
      setMailboxes(current => [...current.filter(box => box.id !== fresh.id), fresh].sort((a, b) => a.name.localeCompare(b.name)))
      choose(fresh); key = requestKey.current
      setParams({ mailbox: fresh.id }, { replace: true })
      if (creating && fresh.provider === 'gmail') {
        setMessage('Casella creata. Collegamento Gmail in avvio?')
        try { await openOAuth(fresh.id) }
        catch (e) { if (key === requestKey.current) { setOauthIncomplete(true); report(e); setMessage('Casella creata. Collegamento Gmail da completare') } }
      } else setMessage('Configurazione salvata. La sincronizzazione è automatica dopo il collegamento.')
    } catch (e) {
      if (key !== requestKey.current) return
      report(e)
      const status = (e as MailApiError).status
      if (creating && !createdId && (!status || status >= 500)) {
        setCreationAmbiguous(true)
        setMessage('Non è possibile confermare la creazione. Ricarica l’elenco e seleziona la casella eventualmente creata, senza ripetere il salvataggio.')
      }
    } finally { saveLock.current = false; if (key === requestKey.current) setBusy(false) }
  }
  const startOAuth = async () => {
    if (!selected || busy || saveLock.current || !selected.permissions['mail.manage']) return
    saveLock.current = true
    const key = requestKey.current
    setBusy(true); setError('')
    try { await openOAuth(selected.id) }
    catch (e) { if (key === requestKey.current) { report(e); setOauthIncomplete(true); setMessage('Casella creata. Collegamento Gmail da completare') } }
    finally { saveLock.current = false; if (key === requestKey.current) setBusy(false) }
  }

  const sync = async () => {
    if (!selected || busy) return
    const key = requestKey.current
    setBusy(true); setError('')
    try {
      const run = await syncMailbox(selected.id)
      if (key !== requestKey.current) return
      setMessage('Sincronizzazione richiesta. Controlla lo stato delle sincronizzazioni recenti.')
      setPollCycle(current => current + 1)
    } catch (err) { if (key === requestKey.current) report(err) }
    finally { setBusy(false) }
  }

  const controls: Array<[keyof MailboxInput, string, string]> = [
    ['name', 'Nome', 'text'], ['address', 'Indirizzo email', 'email'], ['username', 'Utente IMAP/SMTP', 'text'],
    ['imap_host', 'Host IMAP', 'text'], ['imap_port', 'Porta IMAP', 'number'], ['smtp_host', 'Host SMTP', 'text'],
    ['smtp_port', 'Porta SMTP', 'number'], ['initial_since', 'Storico iniziale dal (obbligatorio)', 'date'],
    ['max_messages_per_run', 'Messaggi massimi per sync', 'number'], ['max_message_bytes', 'Dimensione massima messaggio (byte)', 'number'],
    ['max_attachment_bytes', 'Dimensione massima allegato (byte)', 'number'], ['sync_interval_seconds', 'Intervallo sync (secondi)', 'number'],
    ['timeout_seconds', 'Timeout (secondi)', 'number'],
  ]
  const select = (key: keyof MailboxInput, label: string, choices: Array<[string, string]>) => <div key={key} className="space-y-1">
    <Label htmlFor={`mail-${key}`}>{label}</Label><select id={`mail-${key}`} value={String(form[key])} onChange={e => update(key, e.target.value)} className="w-full rounded border p-2 bg-background" disabled={busy}>
      {choices.map(([value, text]) => <option key={value} value={value}>{text}</option>)}
    </select>{fieldErrors[key] && <p role="alert">{fieldErrors[key]}</p>}
  </div>

  const renderControls = (subset: typeof controls) => <>
{subset.map(([key, label, type]) => <div key={key} className="space-y-1"><Label htmlFor={`mail-${key}`}>{label}</Label>
                <Input id={`mail-${key}`} type={type} value={String(form[key] ?? '')} onChange={e => update(key, numeric.has(key) ? Number(e.target.value) : e.target.value || (key === 'initial_since' ? null : ''))} disabled={busy} required={['name', 'address', 'initial_since'].includes(key)} />
                {['max_message_bytes', 'max_attachment_bytes'].includes(key) && <p className="text-sm">{Number(form[key])} byte = {new Intl.NumberFormat('it-IT', { maximumFractionDigits: 6 }).format(Number(form[key]) / 1048576)} MiB</p>}
                {['sync_interval_seconds', 'timeout_seconds'].includes(key) && <p className="text-sm">{Math.floor(Number(form[key]) / 60)} minuti e {Number(form[key]) % 60} secondi</p>}
                {fieldErrors[key] && <p role="alert">{fieldErrors[key]}</p>}
              </div>)}
</>
  const identityControls = controls.filter(([key]) => ['name', 'address'].includes(key))
  const connectionControls = controls.filter(([key]) => ['username', 'imap_host', 'imap_port', 'smtp_host', 'smtp_port', 'initial_since'].includes(key))
  const advancedControls = controls.filter(([key]) => ![...identityControls, ...connectionControls].some(([known]) => key === known))
  const next = () => {
    if (step === 1) {
      for (const key of ['name', 'address'] as const) {
        const input = document.getElementById(`mail-${key}`) as HTMLInputElement | null
        if (!form[key].trim() || (input && !input.checkValidity())) { setFieldErrors({ [key]: 'Inserisci un valore valido.' }); return }
      }
    }
    setFieldErrors({}); setStep(value => Math.min(3, value + 1))
  }
  const steps = ['Provider', 'Identità e visibilità', 'Connessione e cartelle', 'Riepilogo']
  return <AppShell currentUser={user}><div className="mail-page mail-settings space-y-5">
    <MailHeader title="Caselle mail" description="Collega i tuoi account e gestisci la sincronizzazione." />
    {loading && <p role="status">Caricamento caselle…</p>}
    {error && <Alert variant="destructive" role="alert"><AlertDescription>{error}</AlertDescription></Alert>}
    {message && <Alert role="status"><AlertDescription>{message}</AlertDescription></Alert>}
    {creationAmbiguous && <Button variant="outline" disabled={busy} onClick={reload}>Ricarica elenco caselle</Button>}
    {conflict && <Button type="button" onClick={reload}>Rileggi configurazione aggiornata</Button>}
    {!loading && mailboxes.length === 0 && <p>Nessuna casella configurata. Crea un account per iniziare.</p>}
    <div className="grid gap-5 lg:grid-cols-[16rem_1fr]">
      <nav aria-label="Caselle" className="space-y-2">
        <Button type="button" variant="outline" disabled={busy} onClick={() => { choose(null); setParams({}) }}>Nuova casella</Button>
        {mailboxes.map(box => <Button key={box.id} type="button" variant={selectedId === box.id ? 'default' : 'outline'} className="w-full justify-start" disabled={busy} onClick={() => { choose(box); setParams({ mailbox: box.id }) }}>{box.name}<span aria-hidden="true" className="ml-2 text-xs">{indexingLabel(box)}</span></Button>)}
      </nav>
      <div className="space-y-5">
        {selected && <Card><CardHeader><CardTitle>Stato connessione</CardTitle></CardHeader><CardContent className="space-y-2">
          <p>{selected.has_credentials ? 'Credenziali presenti' : 'Collegamento mancante'}</p>
          {selected.connection_error && <><p role="alert">{selected.connection_error === 'oauth_revoked' ? 'Collegamento Gmail revocato o scaduto. Riprendi il collegamento.' : 'Non è stato possibile sincronizzare la casella. Verifica la configurazione o riprova più tardi.'}</p><details><summary>Dettagli connessione</summary><p>{selected.connection_error}</p></details></>}
          {!selected.last_sync_at && <p>La connessione non è ancora stata verificata dalla sincronizzazione.</p>}
          <p>Ultima sync: {selected.last_sync_at ? new Date(selected.last_sync_at).toLocaleString() : 'Mai eseguita'}</p>
          {!selected.has_credentials && <p>Configura la password o collega Gmail per avviare la sincronizzazione.</p>}
          <div className="flex flex-wrap gap-2">
            {selected.provider === 'gmail' && selected.permissions['mail.manage'] && <Button type="button" disabled={busy} onClick={startOAuth}>{oauthIncomplete ? 'Riprendi collegamento' : 'Collega Gmail'}</Button>}
            {canSync && <Button type="button" disabled={busy} onClick={sync}>Sincronizza ora</Button>}
          </div>
        </CardContent></Card>}
        {selected && <section aria-label="Stato indicizzazione AI" className="space-y-2">
          <p>{indexingLabel(selected)}</p>
          {selected.ai_indexing_blocked_reason === 'global_disabled' && <p>Indicizzazione AI bloccata dalla configurazione generale.</p>}
          {indexing.loading && <p role="status">Aggiornamento dello stato dell'indicizzazione…</p>}
          {indexing.error && <div role="alert"><p>{indexing.error}</p><Button type="button" disabled={indexing.loading || indexing.pending} onClick={indexing.refresh}>Riprova</Button></div>}
          {indexingStatus && !indexing.valid && <p>Dati di indicizzazione non aggiornati.</p>}
          {indexing.notice && <p role="status">{indexing.notice}</p>}
        </section>}
        {mayManage && <Card><CardHeader><CardTitle>{selected ? 'Configurazione casella' : 'Nuova casella'}</CardTitle></CardHeader><CardContent>
          <form onSubmit={event => { if (!selected && step < 3) { event.preventDefault(); next() } else void save(event) }} className="space-y-4">
            {!selected && <ol aria-label="Passaggi configurazione" className="flex flex-wrap gap-3">{steps.map((label, index) => <li key={label} aria-current={step === index ? 'step' : undefined}>{index + 1}. {label}</li>)}</ol>}
            {(selected || step === 0) && <section className="space-y-3"><h3>Provider</h3>{select('provider', 'Provider', [['generic', 'IMAP/SMTP'], ['gmail', 'Gmail']])}{form.provider === 'gmail' && <p>Gmail viene collegato tramite consenso OAuth.</p>}</section>}
            {(selected || step === 1) && <section className="grid gap-4 md:grid-cols-2"><h3 className="md:col-span-2">Identità e visibilità</h3>    <label className="block"><input type="checkbox" checked={form.ai_indexing_enabled} disabled={busy} onChange={e => setForm(current => ({ ...current, ai_indexing_enabled: e.target.checked }))} /> Indicizzazione AI delle email</label>
    <p className="text-sm">Le elaborazioni AI possono comportare costi. Lo storico richiede un avvio separato. Gli indici esistenti vengono conservati. Una chiamata AI già in corso può terminare dopo lo spegnimento.</p>
    {indexingStatus && <div>
      <p>{indexingStatus.ready} email indicizzate; {indexingStatus.to_process} da elaborare.</p>
      {indexingStatus.batches.map(batch => <MailIndexProgress key={batch.id} batch={batch} />)}
      <Button type="button" disabled={busy || indexing.pending || !indexing.valid || indexing.forbidden || !selected?.ai_indexing_enabled || !indexingStatus.effective} onClick={indexing.confirm}>Indicizza email già sincronizzate</Button>
      {indexing.confirming && <div role="alertdialog" aria-label="Conferma indicizzazione storico">
        {!indexing.preview ? <p>Caricamento anteprima aggiornata…</p> : <>
          <p>{{ start: 'Avvia storico', resume: 'Riprendi storico sospeso', existing: 'Storico già in lavorazione', none: 'Nessuna email da elaborare' }[indexing.preview.kind]}</p>
          <p>{indexing.preview.eligible} email eleggibili; {indexing.preview.ready} aggiornate; {indexing.preview.to_process} da elaborare.</p>
          {indexing.preview.kind === 'resume' && <p>Verrà ripreso un solo batch sospeso.</p>}
          <p>Il perimetro esclude le nuove email successive al limite dello storico. Le elaborazioni AI possono comportare costi.</p>
          {['start', 'resume'].includes(indexing.preview.kind) && <Button type="button" disabled={busy || indexing.pending || !indexing.valid || indexing.forbidden || !indexing.preview.allowed} onClick={() => void indexing.start()}>{indexing.pending ? 'Avvio in corso…' : 'Conferma'}</Button>}
        </>}
        <Button type="button" onClick={indexing.close}>Annulla</Button>
      </div>}
    </div>}              {renderControls(identityControls)}{select('visibility', 'Visibilità', [['personal', 'Personale'], ['shared', 'Condivisa']])}<p>Personale indica la tua casella; Condivisa indica l’ambito aziendale. L’accesso dipende dai permessi assegnati.</p></section>}
            {(selected || step === 2) && <section className="space-y-4"><h3>Connessione e cartelle</h3>
            <fieldset className="space-y-2 rounded border p-4" disabled={busy}>
              <legend className="font-semibold">Posta inviata</legend>
              <Label htmlFor="mail-sent_folder">Cartella inviati</Label>
              <Input id="mail-sent_folder" list="mail-folder-options" value={form.sent_folder}
                aria-describedby="mail-sent-help" placeholder="Nome esatto della cartella IMAP"
                onChange={e => update('sent_folder', e.target.value)} />
              <datalist id="mail-folder-options">
                {Array.from(new Set(folderText.split(/\r?\n/).map(x => x.trim()).filter(Boolean))).map(name => <option key={name} value={name} />)}
              </datalist>
              <p id="mail-sent-help" className="text-sm text-muted-foreground">Scegli una delle cartelle configurate oppure inserisci il nome esatto sul server, rispettando maiuscole e minuscole. Al salvataggio verrà inclusa automaticamente nelle cartelle da sincronizzare.</p>
              {!form.sent_folder.trim() && <p role="status" className="text-sm">Puoi leggere la posta, ma devi configurare la cartella Inviati per inviare.</p>}
              {fieldErrors.sent_folder && <p role="alert">{fieldErrors.sent_folder}</p>}
            </fieldset>
              <div className="grid gap-4 md:grid-cols-2">{renderControls(connectionControls)}
              {select('auth_kind', 'Autenticazione', [['password', 'Password'], ['oauth2', 'OAuth 2.0']])}
              {select('imap_security', 'Sicurezza IMAP', [['tls', 'TLS'], ['starttls', 'STARTTLS']])}
              {select('smtp_security', 'Sicurezza SMTP', [['tls', 'TLS'], ['starttls', 'STARTTLS']])}
              <div className="space-y-1"><Label htmlFor="mail-folders">Cartelle ammesse, una per riga</Label><textarea id="mail-folders" value={folderText} onChange={e => setFolderText(e.target.value)} disabled={busy} className="w-full rounded border p-2 bg-background" rows={3} /><p className="text-sm text-muted-foreground">Cartelle esistenti da leggere nel CRM. INBOX contiene la posta ricevuta; la cartella Inviati scelta sopra viene inclusa automaticamente. Questo elenco non crea cartelle sul server.</p>{fieldErrors.folders && <p role="alert">{fieldErrors.folders}</p>}</div>
              {form.auth_kind === 'password' && <div className="space-y-1"><Label htmlFor="mail-password">Password {selected ? '(lascia vuoto per mantenere)' : ''}</Label><Input id="mail-password" type="password" autoComplete="new-password" value={form.password || ''} onChange={e => update('password', e.target.value)} disabled={busy} />{fieldErrors.password && <p role="alert">{fieldErrors.password}</p>}</div>}
              </div>
              <details open={advancedOpen} onToggle={event => setAdvancedOpen(event.currentTarget.open)}><summary>Avanzate</summary><div className="grid gap-4 md:grid-cols-2 pt-3">{renderControls(advancedControls)}</div></details>
            </section>}
            {!selected && step === 3 && <section aria-label="Riepilogo configurazione" className="space-y-2"><h3>Riepilogo</h3><p>Provider: {form.provider === 'gmail' ? 'Gmail' : 'IMAP/SMTP'}</p><p>Nome: {form.name}</p><p>Indirizzo: {form.address}</p><p>Visibilità: {form.visibility === 'personal' ? 'Personale' : 'Condivisa'}</p><p>Cartelle: {Array.from(new Set([...folderText.split(/\r?\n/).map(value => value.trim()).filter(Boolean), ...(form.sent_folder.trim() ? [form.sent_folder.trim()] : [])])).join(', ') || 'Nessuna cartella configurata'}</p></section>}
            {!selected && <div className="flex gap-2">{step > 0 && <Button type="button" variant="outline" disabled={busy} onClick={() => setStep(value => value - 1)}>Indietro</Button>}{step < 3 && <Button type="button" disabled={busy} onClick={next}>Avanti</Button>}</div>}
            {(selected || step === 3) && <Button type="submit" disabled={busy || conflict || creationAmbiguous}>{busy ? 'Salvataggio…' : (!selected && form.provider === 'gmail' ? 'Salva e collega Gmail' : 'Salva configurazione')}</Button>}
          </form>
        </CardContent></Card>}
        {selected && !mayManage && <p>Puoi visualizzare la configurazione autorizzata, ma non modificarla.</p>}
        {selected?.permissions['mail.read'] && <Card><CardHeader><CardTitle>Sincronizzazioni recenti</CardTitle></CardHeader><CardContent>
          {runs.length === 0 ? <p>Nessuna sincronizzazione registrata.</p> : <ul className="space-y-2">{runs.map(run => <li key={run.id} className="border-b pb-2"><strong>{{ queued: 'In attesa', running: 'Sincronizzazione in corso', succeeded: 'Sincronizzazione riuscita', partial: 'Sincronizzazione parziale', failed: 'Sincronizzazione non riuscita', interrupted: 'Sincronizzazione interrotta' }[run.status]}</strong> · {new Date(run.created_at).toLocaleString()}<details><summary>Dettagli sincronizzazione</summary><p>ID: {run.id}</p><p>Acquisiti {run.acquired_count}, parziali {run.partial_count}, replay {run.replayed_count}</p>{run.error_code && <p>Codice: {run.error_code}</p>}</details></li>)}</ul>}

        </CardContent></Card>}
      </div>
    </div>
  </div></AppShell>
}
